'use client';

import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { cx } from 'class-variance-authority';
import type { MotionProps } from 'framer-motion';
import { useTranslations } from 'next-intl';

import Chatbot from '@/app/components/Chatbot';
import Motion from '@/app/components/Motion';

import ProductInfoBar from '../ProductInfoBar';
import type { FloatingBottomBarProps } from './types';

export const getProductInfoBarMotionProps = ({
  isVisible,
}: {
  isVisible: boolean;
}) => {
  return {
    animate: {
      opacity: isVisible ? 1 : 0,
      y: isVisible ? 0 : 'var(--product-info-bar-motion-y)',
    },
    initial: {
      opacity: 0,
      y: 'var(--product-info-bar-motion-y)',
    },
    transition: {
      duration: 0.75,
    },
  };
};

export const getChatbotMotionProps = ({
  isProductInfoBarVisible,
}: {
  isProductInfoBarVisible: boolean;
}): MotionProps => {
  return {
    initial: {
      top: isProductInfoBarVisible
        ? 'var(--chatbot-motion-top-with-bar)'
        : 'var(--chatbot-motion-top-alone)',
      bottom: isProductInfoBarVisible
        ? 'var(--chatbot-motion-bottom-with-bar)'
        : 'var(--chatbot-motion-bottom-alone)',
      y: 'var(--chatbot-motion-y)',
      width: 'auto',
    },
    animate: {
      top: isProductInfoBarVisible
        ? 'var(--chatbot-motion-top-with-bar)'
        : 'var(--chatbot-motion-top-alone)',
      bottom: isProductInfoBarVisible
        ? 'var(--chatbot-motion-bottom-with-bar)'
        : 'var(--chatbot-motion-bottom-alone)',
    },
    transition: { duration: 0.75 },
  };
};

const FloatingBottomBar = ({
  canDisplayProductInfoBar,
  canDisplayChatbot,
  isProductInfoBarVisible,
  productInfoBarProps,
  hideChatbotTriggerButton,
}: FloatingBottomBarProps) => {
  const t = useTranslations();

  return (
    <LayoutGrid className="pointer-events-none fixed bottom-4 left-0 z-[90] w-full">
      <GridItem
        colEnd={{ sm: 'main', md: 'full' }}
        colStart={{ sm: 'main', md: 'full' }}
      >
        <div className="relative md:px-24">
          {canDisplayProductInfoBar && (
            <Motion
              className={cx(
                'mx-auto w-full max-w-[1254px] [--product-info-bar-motion-y:100] md:[--product-info-bar-motion-y:20]',
                {
                  'pointer-events-auto': isProductInfoBarVisible,
                },
              )}
              {...getProductInfoBarMotionProps({
                isVisible: isProductInfoBarVisible,
              })}
            >
              <ProductInfoBar {...productInfoBarProps} />
            </Motion>
          )}

          {canDisplayChatbot && (
            <Motion
              className={cx(
                'absolute right-0 flex size-10 items-center md:size-12',
                /**
                 * Using CSS variables to avoid hydration mismatch issues; these variables allow the chatbot to slide up
                 * and make room for the ProductInfoBar on mobile, while effectively turning off the animation on medium
                 * and larger screens (since the chatbot is side-by-side with the ProductInfoBar rather than above it in
                 * those cases).
                 */
                '[--chatbot-motion-top-alone:auto] md:[--chatbot-motion-top-alone:50%]',
                '[--chatbot-motion-bottom-alone:0] md:[--chatbot-motion-bottom-alone:auto]',
                '[--chatbot-motion-top-with-bar:-56px] md:[--chatbot-motion-top-with-bar:50%]',
                '[--chatbot-motion-bottom-with-bar:auto] md:[--chatbot-motion-bottom-with-bar:auto]',
                '[--chatbot-motion-y:unset] md:[--chatbot-motion-y:-50%]',
              )}
              {...getChatbotMotionProps({ isProductInfoBarVisible })}
            >
              <Chatbot
                className={cx(
                  'absolute right-0 md:right-6',
                  !hideChatbotTriggerButton && 'pointer-events-auto',
                )}
                chatOpenClassName="fixed bottom-0 md:absolute"
                initialMessage={t('chatbot_initial_message_or4_pdp')}
                shouldHideTriggerButton={hideChatbotTriggerButton}
              />
            </Motion>
          )}
        </div>
      </GridItem>
    </LayoutGrid>
  );
};

export default FloatingBottomBar;
